import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
    Home,
    BarChart2,
    Car,
    PhoneCall,
    Wrench,
    LogOut,
    Menu,
    X,
    LayoutDashboard
} from "lucide-react";
import { useDispatch } from "react-redux";
import { onLogout } from "../../Auth/Actions";

const logoImage1 = "https://firebasestorage.googleapis.com/v0/b/mgcentral-a28cf.firebasestorage.app/o/logo%2Fmg-icon-006.webp?alt=media&token=cc8c9019-2e23-443d-9e0a-206b6380069b";

const Sidebar = ({ open, setOpen }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 1024;
            setIsMobile(mobile);
            if (!mobile) setOpen(true);
            else setOpen(false);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [setOpen]);

    const handleLogout = () => {
        dispatch(onLogout());
        localStorage.clear();
        navigate("/admin");
    };

    const sidebarStyle = {
        width: open ? "260px" : isMobile ? "0px" : "80px",
        height: "100vh",
        position: "fixed",
        left: 0,
        top: 0,
        background: "var(--bg-sidebar)",
        color: "#fff",
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        overflowX: "hidden",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        boxShadow: open ? "10px 0 30px rgba(0,0,0,0.3)" : "none",
    };

    const overlayStyle = {
        display: isMobile && open ? "block" : "none",
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(0,0,0,0.5)",
        backdropFilter: "blur(4px)",
        zIndex: 999,
        transition: "0.3s",
    };

    const menu = [
        { label: "Dashboard", path: "/admin", icon: <LayoutDashboard size={20} /> },
        { label: "Advertisement", path: "/advertisement", icon: <BarChart2 size={20} /> },
        { label: "Test Drive", path: "/book-test-drive", icon: <PhoneCall size={20} /> },
        { label: "Contact Details", path: "/contactUs-details", icon: <PhoneCall size={20} /> },
        { label: "Service Request", path: "/service-request", icon: <Wrench size={20} /> },
        { label: "Vehicle Enquiry", path: "/vehicle-enquiry", icon: <Car size={20} /> },
        { label: "Old Cars", path: "/oldcar", icon: <Car size={20} /> },
    ];

    return (
        <>
            <div style={overlayStyle} onClick={() => setOpen(false)} />

            <div style={sidebarStyle}>
                {/* Logo & Toggle */}
                <div style={{
                    padding: "24px 20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: open ? "space-between" : "center",
                    borderBottom: "1px solid rgba(255,255,255,0.05)"
                }}>
                    {open ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <img src={logoImage1} alt="Logo" style={{ width: "32px", height: "32px", borderRadius: "8px" }} />
                            <span style={{ fontWeight: "700", fontSize: "18px", letterSpacing: "0.5px", fontFamily: "Outfit" }}>
                                MG CENTRAL
                            </span>
                        </div>
                    ) : (
                        <img src={logoImage1} alt="Logo" style={{ width: "32px", height: "32px" }} />
                    )}

                    {!isMobile && (
                        <button
                            onClick={() => setOpen(!open)}
                            style={{
                                background: "rgba(255,255,255,0.1)",
                                border: "none",
                                borderRadius: "8px",
                                color: "#fff",
                                cursor: "pointer",
                                width: "32px",
                                height: "32px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "0.2s"
                            }}
                        >
                            {open ? <X size={16} /> : <Menu size={16} />}
                        </button>
                    )}
                </div>

                {/* Profile Section */}
                {open && (
                    <div style={{
                        padding: "24px 20px",
                        margin: "12px 16px",
                        borderRadius: "16px",
                        background: "rgba(255,255,255,0.03)",
                        border: "1px solid rgba(255,255,255,0.05)",
                        display: "flex",
                        alignItems: "center",
                        gap: "12px"
                    }}>
                        <div style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "12px",
                            background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            fontSize: "18px"
                        }}>
                            A
                        </div>
                        <div style={{ overflow: "hidden" }}>
                            <div style={{ fontSize: "14px", fontWeight: "600", whiteSpace: "nowrap" }}>Admin Panel</div>
                            <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.5)", whiteSpace: "nowrap" }}>Administrator</div>
                        </div>
                    </div>
                )}

                {/* Navigation Items */}
                <div style={{ flex: 1, padding: "10px 16px", display: "flex", flexDirection: "column", gap: "4px" }}>
                    {menu.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            onClick={() => isMobile && setOpen(false)}
                            style={({ isActive }) => ({
                                display: "flex",
                                alignItems: "center",
                                padding: "14px 16px",
                                borderRadius: "12px",
                                color: isActive ? "#fff" : "rgba(255,255,255,0.6)",
                                background: isActive ? "linear-gradient(90deg, var(--accent) 0%, rgba(59, 130, 246, 0.5) 100%)" : "transparent",
                                textDecoration: "none",
                                transition: "0.2s",
                                transitionProperty: "background, color, transform"
                            })}
                        >
                            <div style={{ minWidth: "24px", display: "flex", justifyContent: "center" }}>
                                {item.icon}
                            </div>
                            {open && <span style={{ marginLeft: "12px", fontSize: "14px", fontWeight: "500" }}>{item.label}</span>}
                        </NavLink>
                    ))}
                </div>

                {/* Logout Button */}
                <div style={{ padding: "20px 16px", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                    <button
                        onClick={handleLogout}
                        style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            padding: "14px 16px",
                            borderRadius: "12px",
                            color: "rgba(255,255,255,0.6)",
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            transition: "0.2s"
                        }}
                    >
                        <div style={{ minWidth: "24px", display: "flex", justifyContent: "center" }}>
                            <LogOut size={20} />
                        </div>
                        {open && <span style={{ marginLeft: "12px", fontSize: "14px", fontWeight: "500" }}>Logout</span>}
                    </button>
                </div>
            </div>
        </>
    );
};

export default Sidebar;

















